<template>
  <InfoCard>
    <div v-if="title" class="artifacts-header">
      <h1 class="artifacts-header__title">{{ title }}</h1>
      <HelpTip class="artifacts-header__help" :isInPlace="true">
        For the latest supported model list, per-preset accuracy benchmarks, and validation results on various edge platforms, see the
        <a href="https://huggingface.co/spaces/EdgeFirst/Models" target="_blank" rel="noopener noreferrer">EdgeFirst Model Zoo</a>
        on Hugging Face.
      </HelpTip>
    </div>
    <div class="artifacts scrollbar">
      <template v-if="artifacts && artifacts.length > 0">
        <DownloadModelCard
          v-for="(artifact, i) in artifacts"
          :key="i"
          :title="artifact.name"
          :modelType="artifact.modelType"
          :showRunModel="isOnnxArtifact(artifact)"
          class="download-model-card"
          :class="`${artifact.name}-btn`"
          @download="handleDownloadModel(artifact)"
          @run-model="handleRunModel(artifact)"
        />
      </template>
      <template v-else>
        No model files
      </template>
    </div>
  </InfoCard>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import DownloadModelCard from '@/components/TrainerComponent/TrainerExperimentAndSessions/DownloadModelCard.vue';
import HelpTip from '@/components/HelpTip.vue';
import InfoCard from '@/components/InfoCard.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  sessionId: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'artifacts-loaded']);

const router = useRouter();
const artifacts = ref([]);

function isOnnxArtifact(artifact) {
  return artifact?.name?.toLowerCase().endsWith('.onnx');
}

async function getArtifacts(sessionId) {
  if (!sessionId) {
    artifacts.value = [];
    return;
  }

  try {
    const dataConnect = new DatastoreConnect();
    const resp = await dataConnect.getTrainingSessionArtifacts({
      training_session_id: parseInt(sessionId),
    });

    if (resp.error !== undefined) {
      throw Error(resp.error.message);
    }

    artifacts.value = resp.result || [];
  } catch (error) {
    console.error('Error fetching artifacts:', error);
    artifacts.value = [];
  }

  emit('update:modelValue', artifacts.value);
  emit('artifacts-loaded', artifacts.value);
}

function handleDownloadModel(model) {
  const url = `/download_model?training_session_id=${props.sessionId}&file=${encodeURIComponent(model.name)}`;

  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', '');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function handleRunModel(model) {
  router.push({
    name: 'model_runner',
    query: {
      trainer: props.sessionId,
      model: model.name,
    },
  });
}

watch(() => props.sessionId, (newVal) => {
  if (newVal) {
    getArtifacts(newVal);
  }
});

onMounted(() => {
  if (props.sessionId) {
    getArtifacts(props.sessionId);
  }
});
</script>

<style scoped>
.artifacts-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin: 0 0 8px 0;
}

.artifacts-header__title {
  font-size: 1.12rem;
  font-weight: 600;
  margin: 0;
  color: var(--color-primary-500, #1976d2);
}

.artifacts-header__help {
  width: 100%;
}

.artifacts {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
  padding: 4px;
}
</style>
